/* eslint-disable @typescript-eslint/no-explicit-any */
import { useNavigate } from "react-router-dom";
import { InputWithLabel } from "@/pages/components/input-label";
import { useForm } from "@tanstack/react-form";
import { groupPageSchema } from "@/lib/form-schema";
import { cn, describeMissingFields } from "@/lib/utils";
import useGroup from "@/hooks/use-group";
import { useGroupProvider } from "@/context/group-provider";
import { useNotificationError } from "@/context/error-provider";
import { ErrorNotificationType } from "./components/error-notification";
import ScreenRouteNames from "@/constants/screen-route-names";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { useFocusOnOpen } from "@/hooks/use-focus-on-open";
import LoginSupportChatbot from "./login-support-chatbot";
import { useTheme } from "@/context/theme-provider";
import { getFlavourFor } from "@/config/product-config";
import type { ExtraData } from "@/types";
import {
  LOGIN_SUPPORT_CHATBOT_ACTIONS,
  STORAGE_KEY,
  STORAGE,
} from "@/constants";
import { setItem } from "@/lib/storage-utils";
import IDENTIFIERS from "@/constants/test_id";
import { usePanelLogger } from "@/services/logger/logger-hooks";
import {
  LogCategories,
  LogEventTypeEnum,
  LogSubCategories,
  LogTags,
} from "@/services/logger/logger-types";
import { PanelTracker } from "@/services/logger/smart-context-provider";
type VerifyGroupFormValues = {
  groupCode: string | undefined;
};

type PanelOpenState = {
  isOpen: boolean;
  extraData: ExtraData | null;
};

const Group = () => {
  const { changeTheme } = useTheme();
  const [isPanelOpen, setIsPanelOpen] = useState<PanelOpenState>({
    isOpen: false,
    extraData: null,
  });
  const { logPanelOpen } = usePanelLogger();
  /**
   * Both triggers unmount while the support content is shown (they're the other branch
   * of the same conditional), so a ref captured at click time would point at a detached
   * node by the time we could restore focus to it — calling .focus() on it is a silent
   * no-op and focus falls to <body> (2.4.3 Focus Order — A). Instead, opening only
   * records *which* trigger's data-testid is owed focus; the effect below restores it
   * once the form section (and the real, freshly-mounted trigger inside it) is back in
   * the DOM.
   */
  const formSectionRef = useRef<HTMLDivElement>(null);
  const pendingFocusTestIdRef = useRef<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closePanel = () => {
    setIsPanelOpen({ isOpen: false, extraData: null });
  };
  useEffect(() => {
    if (isPanelOpen.isOpen || !pendingFocusTestIdRef.current) return;
    const testId = pendingFocusTestIdRef.current;
    pendingFocusTestIdRef.current = null;
    formSectionRef.current
      ?.querySelector<HTMLElement>(`[data-testid="${testId}"]`)
      ?.focus();
  }, [isPanelOpen.isOpen]);
  // 2.4.3 Focus Order — A: moves focus into the support content when it replaces the
  // page, and lets Escape act as a shortcut back — not a focus trap, since nothing here
  // is a modal overlay blocking the rest of the page.
  useFocusOnOpen(panelRef, isPanelOpen.isOpen, closePanel);
  const navigate = useNavigate();
  const { setGroupData } = useGroupProvider();
  const { addError } = useNotificationError();

  const form = useForm({
    defaultValues: {
      groupCode: "",
    },
    onSubmit: async ({ value }) => {
      handleGroupVerification(value);
    },
  });
  const groupQuery = useGroup();

  const handleGroupVerification = (value: VerifyGroupFormValues) => {
    groupQuery.mutate(value.groupCode!, {
      onSuccess: (data) => {
        // console.log("group data: ", data)
        if (data?.data.length === 0) {
          addError({
            message: data.summary.status_message,
            type: ErrorNotificationType.ERROR,
          });
        } else if (data.data) {
          // console.log("Which client: ", data.data?.values?.group_id)
          // console.log("Which theme: ", checkBrandingClient(data.data?.values?.group_id))
          const flavour = getFlavourFor(
            data.data?.values?.group_id,
            data.data?.values?.division_id,
          );
          if (flavour) {
            changeTheme(flavour.theme);
            (window as any).__PRODUCT_CONFIG__ = flavour;
            setItem(STORAGE_KEY.PRODUCT_CONFIG, flavour, STORAGE.SESSION);
          }

          if (
            Object.prototype.hasOwnProperty.call(data, "registrationFields")
          ) {
            setGroupData({
              ...data.data.values,
              registrationFieldsLookup: data?.registrationFields,
            });
          } else {
            setGroupData(data.data.values);
          }
          //Store group cache data in local for user mode to persist accross new
          setItem(
            STORAGE_KEY.CACHED_GROUP_DETAIL,
            {
              groupId: data.data.values.entered_id,
            },
            STORAGE.LOCAL,
          );

          navigate(`/${ScreenRouteNames.DISCLAIMER.path}`, { replace: true });
        }
      },
      onError: (error) => {
        addError({
          message: error.message,
          type: ErrorNotificationType.ERROR,
        });
      },
    });
  };

  return (
    <main className="flex h-full flex-col items-center justify-center px-10">
      {isPanelOpen.isOpen ? (
        // 4.1.2 Name, Role, Value — A / 2.4.3 Focus Order — A: the support content
        // replaces the page in place — no slide-up panel, no overlay, no dialog
        // semantics, because nothing here is a modal floating over the page.
        // useFocusOnOpen moves focus into it (LoginSupportChatbot's own <h1> becomes
        // the page's heading while this is shown) and wires Escape as a shortcut back.
        <div ref={panelRef} className="size-full">
          <PanelTracker
            panelName="LOGIN_SUPPORT_CHATBOT"
            category={LogCategories.CHATBOT}
            subCategory={LogSubCategories.NONE}
            tag={LogTags.SUPPORT}
          >
            <LoginSupportChatbot
              rawUrlParams={isPanelOpen?.extraData as ExtraData}
              onClose={closePanel}
            />
          </PanelTracker>
        </div>
      ) : (
        // display: contents keeps this wrapper out of the flex layout while still
        // giving the focus-restoration effect above a DOM node to query a trigger
        // back out of. space-y lives here (not on <main>) because Tailwind's sibling
        // selector matches real DOM children — display: contents only flattens the
        // box tree for flex layout, it doesn't change DOM adjacency.
        <div ref={formSectionRef} className="contents space-y-10">
          <div className="mb-2 h-16 max-w-40">
            {/*
              1.1.1 Non-text Content — A. alt="logo" described the image's kind, not its
              meaning. The logo is branding that carries no information the adjacent
              heading does not already give, so it is marked decorative.
            */}
            <img
              src={(window as any).__PRODUCT_CONFIG__?.logo}
              alt=""
              className="h-full object-contain"
              data-testid={IDENTIFIERS.GROUP_SCREEN.APP_LOGO}
            />
          </div>
          <div className="text-center">
            {/* 1.3.1 Info and Relationships — A: the page's outline must start at h1.
                The size comes from the Tailwind class, so this is visually unchanged. */}
            <h1 className="text-sm font-medium">
              Please enter your group code
            </h1>
          </div>

          {/*
            4.1.3 Status Messages — AA. Mounted unconditionally so the text change is
            announced; a live region that mounts together with its message announces
            nothing.
          */}
          <div role="status" aria-live="polite" className="sr-only">
            {groupQuery.isPending ? "Verifying your group code" : ""}
          </div>
          <form
            className="w-auto space-y-4 sm:w-64"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
          >
            <div>
              <form.Field
                name="groupCode"
                validators={{
                  onBlur: ({ value }) => {
                    const result =
                      groupPageSchema.shape.groupCode.safeParse(value);
                    return result.success
                      ? undefined
                      : result.error.errors[0].message;
                  },
                }}
              >
                {(field) => (
                  <div className="flex flex-row items-center space-x-2">
                    <InputWithLabel
                      id="groupCode"
                      // 3.3.2 Labels or Instructions — A: a placeholder is not a label; it
                      // disappears as soon as the member starts typing.
                      label="Group Code"
                      placeholder="Group Code"
                      value={field.state.value}
                      onChange={(newValue) => field.handleChange(newValue)}
                      onBlur={() => field.handleBlur()}
                      error={field.state.meta.errors[0]}
                      data-testid={IDENTIFIERS.GROUP_SCREEN.GROUP_CODE_INPUT}
                    />
                  </div>
                )}
              </form.Field>
            </div>
            {/*
          2.1.1 Keyboard — A / 4.1.2 Name, Role, Value — A. This was a <div onClick>: not in
          the tab order, no role, no Enter/Space activation, so a member who cannot use a
          mouse could not reach the only help on this screen.
          type="button" is required — this sits inside the <form>, and a bare <button>
          defaults to type="submit" and would submit the form instead of opening the panel.
        */}
            <button
              type="button"
              onClick={() => {
                pendingFocusTestIdRef.current =
                  IDENTIFIERS.GROUP_SCREEN.GROUP_LOCATE_LINK;
                logPanelOpen(
                  IDENTIFIERS.GROUP_SCREEN.GROUP_LOCATE_LINK,
                  {},
                  {
                    tag: LogTags.SUPPORT,
                    category: LogCategories.CHATBOT,
                    subCategory: LogSubCategories.NONE,
                    type: LogEventTypeEnum.USER_INTERACTION,
                  },
                );
                setIsPanelOpen({
                  isOpen: true,
                  extraData: {
                    group: (window as any).__PRODUCT_CONFIG__?.groupId,
                    action: LOGIN_SUPPORT_CHATBOT_ACTIONS.LOCATE_ACCOUNT,
                  },
                });
              }}
              className="group w-full rounded text-center text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              data-testid={IDENTIFIERS.GROUP_SCREEN.GROUP_LOCATE_LINK}
            >
              <span className="">Group Code Lookup?</span>{" "}
              <span className="font-bold group-hover:underline">
                Click Here
              </span>
            </button>
            <form.Subscribe
              selector={(state) => [state.canSubmit, state.values.groupCode]}
            >
              {([canSubmit, groupCode]) => {
                // 3.3.1 Error Identification — A: names the specific empty field(s)
                // rather than a static list, so the description stays accurate as the
                // member fills the form in.
                const requirement =
                  describeMissingFields([
                    { label: "group code", value: groupCode },
                  ]) || "Disabled. Fix the errors above to continue.";
                return (
                  <>
                    <Button
                      className={cn(
                        "w-full bg-primary hover:bg-primary-selected",
                        !canSubmit && "opacity-50",
                      )}
                      type="submit"
                      data-testid={IDENTIFIERS.GROUP_SCREEN.VERIFY_GROUP_BUTTON}
                      disabled={groupQuery.isPending}
                      aria-disabled={!canSubmit}
                      aria-describedby={
                        !canSubmit ? "verify-group-requirement" : undefined
                      }
                    >
                      {groupQuery.isPending ? "Verifying..." : "Verify Group"}
                    </Button>
                    {!canSubmit && (
                      <p id="verify-group-requirement" className="sr-only">
                        {requirement}
                      </p>
                    )}
                  </>
                );
              }}
            </form.Subscribe>
          </form>
          {/* 2.1.1 Keyboard — A / 4.1.2 — A: was a <div onClick>. See the note above. */}
          <button
            type="button"
            onClick={() => {
              pendingFocusTestIdRef.current =
                IDENTIFIERS.GROUP_SCREEN.LOGIN_SUPPORT_LINK;
              logPanelOpen(
                IDENTIFIERS.GROUP_SCREEN.LOGIN_SUPPORT_LINK,
                {},
                {
                  tag: LogTags.SUPPORT,
                  category: LogCategories.CHATBOT,
                  subCategory: LogSubCategories.NONE,
                  type: LogEventTypeEnum.USER_INTERACTION,
                },
              );
              setIsPanelOpen({
                isOpen: true,
                extraData: {
                  group: (window as any).__PRODUCT_CONFIG__?.groupId,
                },
              });
            }}
            className="group rounded text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            data-testid={IDENTIFIERS.GROUP_SCREEN.LOGIN_SUPPORT_LINK}
          >
            <span className="">Need login Support?</span>{" "}
            <span className="font-bold group-hover:underline">Click Here</span>
          </button>
        </div>
      )}
    </main>
  );
};

export default Group;
